import { Timeframe } from "../models/enum/timeframe.enum";
import { DateHelpers } from "./date-helpers";

export class RealTimeTradeHelpers {
    static getChartBarTimeBoundary(now: Date, timeFrame: Timeframe): ChartBarTimeBoundaryResult {
        if (timeFrame === Timeframe.Day) {
            return this.getDailyBoundaryResult(now, timeFrame);
        } else if (timeFrame === Timeframe.Week) {
            return this.getWeeklyBoundaryResult(now, timeFrame);
        }
        return this.getMinuteOrHourlyBoundaryResult(now, timeFrame);
    }

    private static getDailyBoundaryResult(now: Date, timeFrame: Timeframe): ChartBarTimeBoundaryResult {
        const result = new ChartBarTimeBoundaryResult();
        const marketOpenLocal = DateHelpers.getMarketOpenInLocalTime(now);
        const marketCloseLocal = DateHelpers.getMarketCloseLocalTime(marketOpenLocal);
        result.start = marketOpenLocal;
        result.end = marketCloseLocal;
        return result;
    }

    private static getMinuteOrHourlyBoundaryResult(now: Date, timeFrame: Timeframe): ChartBarTimeBoundaryResult {
        const result = new ChartBarTimeBoundaryResult();
        const minutes = this.getMinutesPerBar(timeFrame);
        result.start = this.roundDown(now, minutes);
        result.end = new Date(result.start.getTime() + minutes * 60000);
        return result;
    }

    private static getWeeklyBoundaryResult(now: Date, timeFrame: Timeframe): ChartBarTimeBoundaryResult {
        const result = new ChartBarTimeBoundaryResult();
        const monday = this.startOfWeek(now, 1); 
        monday.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
        const friday = new Date(monday);
        friday.setDate(monday.getDate() + 4);
        const mondayMarketOpenLocal = DateHelpers.getMarketOpenInLocalTime(monday);
        const fridayMarketCloseLocal = DateHelpers.getMarketCloseLocalTime(friday);
        result.start = mondayMarketOpenLocal;
        result.end = fridayMarketCloseLocal;
        return result;
    }

    static shouldUpdateOrCreateNewBar(timeFrame: Timeframe): boolean {
        const minutes = this.getMinutesPerBar(timeFrame);
        return minutes > 0 || timeFrame === Timeframe.Day || timeFrame === Timeframe.Week;
    }

    static getMinutesPerBar(timeFrame: Timeframe): number {
        const timeFrameMap: { [key in Timeframe]?: number } = {
            [Timeframe.OneMinute]: 1,
            [Timeframe.TwoMinute]: 2,
            [Timeframe.ThreeMinute]: 3,
            [Timeframe.FiveMinute]: 5,
            [Timeframe.TenMinute]: 10,
            [Timeframe.FifteenMinute]: 15,
            [Timeframe.ThirtyMinute]: 30,
            [Timeframe.SixtyMinute]: 60
        };
        return timeFrameMap[timeFrame] ?? 0;
    }

    private static roundDown(date: Date, minutes: number): Date {
        const ms = 1000 * 60 * minutes;
        return new Date(Math.floor(date.getTime() / ms) * ms);
    }

    private static startOfWeek(date: Date, firstDay: number): Date {
        const d = new Date(date);
        const day = d.getDay(), diff = d.getDate() - day + (day === 0 ? -6 : firstDay);
        return new Date(d.setDate(diff));
    }
}

export class ChartBarTimeBoundaryResult {
    public start: Date;
    public end: Date;
}